/**
 * PrePlanStore — the Pre-Planning feature's data-access wrapper.
 *
 * Strict layering: this is the ONLY pre-planning module that talks to the API,
 * and it does so exclusively through the {@link EngineeringVectorApi} facade
 * (`api.documents<PrePlan>('preplans')` + `api.identityProvider`). It never
 * imports ApiClient or calls fetch. React hooks call this store; pages call the
 * hook.
 *
 * Persistence model (mirrors {@link ../documents/documentStore}): each record is
 * one document with id `preplan_<uuid>` in the real 'preplans' generic-store
 * collection (DDB pointer row + S3 body). The short discovery fields
 * (month/title/sprintName/externalLink + timestamps + deleted flag) are mirrored
 * onto the pointer row's index attributes so {@link listSummaries} reconstructs
 * summaries directly from pointer rows WITHOUT hydrating S3 bodies; the Markdown
 * `content` (and `sprintId`) live only in the S3 body and are read on demand by
 * {@link get}. Tenant scoping is automatic — DocumentStore routes by the active
 * team bundle.
 *
 * Deletion: the API layer exposes no delete op, so {@link softDelete} sets a
 * `deleted` flag on the document and its pointer row; queries filter it out.
 *
 * NO backend changes are required: this reuses the generic document store.
 */

import type { DocumentStore, EngineeringVectorApi } from '../../api';
import type { PrePlan, PrePlanDraft, PrePlanSummary } from './types';
import {
  PRE_PLANS_COLLECTION,
  PRE_PLAN_ROW,
  prePlanDocId,
  prePlanIndexAttributes,
  prePlanSummaryFromRow,
} from './prePlanModel';

export class PrePlanStore {
  private readonly docs: DocumentStore<PrePlan>;

  constructor(private readonly api: EngineeringVectorApi) {
    this.docs = api.documents<PrePlan>(PRE_PLANS_COLLECTION);
  }

  /** Lists all non-deleted pre-plan summaries (defensively scoped to this collection). */
  async listSummaries(): Promise<PrePlanSummary[]> {
    const rows = await this.docs.list();
    return rows
      .filter(
        (r) =>
          r[PRE_PLAN_ROW.collection] === PRE_PLANS_COLLECTION &&
          r[PRE_PLAN_ROW.deleted] !== 'true' &&
          !!r[PRE_PLAN_ROW.primaryIdentifier],
      )
      .map((r) => prePlanSummaryFromRow(r));
  }

  /** Loads a single pre-plan record by id (hydrates the S3 body). */
  async get(id: string): Promise<PrePlan | undefined> {
    return this.docs.get(id);
  }

  /**
   * Registers a new pre-plan. Seeds id, createdBy (from identity), createdAt and
   * lastUpdated, persists it, and returns the saved record.
   */
  async create(draft: PrePlanDraft): Promise<PrePlan> {
    const identity = await this.api.identityProvider.getIdentity();
    const plan = this.fromDraft(prePlanDocId(), draft, identity.alias, undefined);
    await this.docs.save(plan.id, plan, prePlanIndexAttributes(plan));
    return plan;
  }

  /**
   * Applies a draft's fields to an existing pre-plan in place (same id/createdBy/
   * createdAt), refreshes lastUpdated, re-mirrors index attributes, and returns
   * the saved record.
   */
  async applyDraft(plan: PrePlan, draft: PrePlanDraft): Promise<PrePlan> {
    const updated = this.fromDraft(plan.id, draft, plan.createdBy, plan.createdAt);
    await this.docs.update(updated.id, updated, prePlanIndexAttributes(updated));
    return updated;
  }

  /**
   * Soft-deletes a pre-plan: fetches it, flags it deleted, and updates both the
   * S3 body and the pointer row so list queries filter it out.
   */
  async softDelete(id: string): Promise<void> {
    const existing = await this.docs.get(id);
    if (!existing) return;
    const deleted: PrePlan = { ...existing, deleted: true };
    await this.docs.update(id, deleted, prePlanIndexAttributes(deleted));
  }

  /**
   * Builds a full PrePlan from a draft, seeding id/createdBy and preserving
   * createdAt on edit. Empty optional text fields collapse to undefined.
   */
  private fromDraft(
    id: string,
    draft: PrePlanDraft,
    createdBy: string,
    existingCreatedAt: string | undefined,
  ): PrePlan {
    const now = new Date().toISOString();
    const clean = (s?: string) => {
      const t = (s ?? '').trim();
      return t ? t : undefined;
    };
    return {
      id,
      month: draft.month.trim(),
      title: draft.title.trim(),
      sprintId: clean(draft.sprintId),
      sprintName: clean(draft.sprintName),
      content: draft.content,
      externalLink: clean(draft.externalLink),
      createdBy,
      createdAt: existingCreatedAt ?? now,
      lastUpdated: now,
      deleted: false,
    };
  }
}
